import { useEffect, useState, useCallback, useMemo } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  CheckCircle2, AlertTriangle, Activity, RefreshCw, Loader2, Mail, MailX, MailCheck,
  CreditCard, Webhook, Send, Truck, Settings2, ChevronRight,
} from "lucide-react";

const fmtRel = (iso) => {
  if (!iso) return "—";
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch (_) { return iso; }
};

/**
 * OCI V2.11 — Readiness Centre.
 * Reuses the EXISTING /platform/live-readiness checks (stripe, webhook, resend,
 * test_email, delivery). No new checks. Presentation only: priority-grouped
 * operational status cards + a single overall verdict + Manage links.
 */
const CHECK_META = {
  // group: critical | comms | config  (drives ordering + severity colour)
  stripe: {
    group: "critical", Icon: CreditCard, name: "Stripe API",
    okMeaning: "Payment key configured — donations can be charged.",
    failMeaning: "No valid Stripe key — donations cannot be charged.",
  },
  webhook: {
    group: "critical", Icon: Webhook, name: "Stripe Webhooks",
    okMeaning: "Webhooks received — payments & subscriptions sync.",
    failMeaning: "No recent webhook — payment confirmations may not sync.",
  },
  resend: {
    group: "comms", Icon: Mail, name: "Resend (Email)",
    okMeaning: "Email provider configured — invites & receipts can send.",
    failMeaning: "No email key — invites & receipts won't send.",
  },
  test_email: {
    group: "comms", Icon: Send, name: "Email Deliverability",
    okMeaning: "Test email delivered in the last 24h.",
    failMeaning: "No recent test email — deliverability unproven.",
  },
  delivery: {
    group: "config", Icon: Truck, name: "Delivery Provider",
    okMeaning: "Active delivery route configured for data exports.",
    failMeaning: "No active delivery provider configured.",
  },
};

const GROUPS = [
  { key: "critical", label: "Revenue Critical" },
  { key: "comms", label: "Customer Communication" },
  { key: "config", label: "Platform Configuration" },
];

export default function LiveReadinessCard({ onManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/platform/live-readiness");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (outcome) => {
    setDrill({ outcome, loading: true, events: [] });
    try {
      const res = await apiClient.get(`/platform/live-readiness/email-events?outcome=${outcome}`);
      setDrill({ outcome, loading: false, events: res.data.events || [] });
    } catch (e) {
      setDrill({ outcome, loading: false, events: [], error: e?.response?.data?.detail || "Failed to load" });
    }
  };

  // Enrich + sort checks by business priority (critical → comms → config).
  const sortedChecks = useMemo(() => {
    if (!data?.checks) return [];
    const order = { critical: 0, comms: 1, config: 2 };
    return data.checks
      .map((c) => ({ ...c, meta: CHECK_META[c.id] || { group: "config", Icon: Settings2, name: c.label, okMeaning: "Configured.", failMeaning: c.hint } }))
      .sort((a, b) => (order[a.meta.group] - order[b.meta.group]));
  }, [data]);

  // Overall verdict: revenue-critical failure → ACTION REQUIRED; comms/config-only → CONDITIONAL.
  const overall = useMemo(() => {
    if (!data?.checks) return null;
    const failing = data.checks.filter((c) => !c.done);
    if (failing.length === 0) return { state: "READY", tone: "emerald", label: "READY" };
    const criticalFail = failing.some((c) => (CHECK_META[c.id]?.group) === "critical");
    if (criticalFail) return { state: "ACTION", tone: "rose", label: "ACTION REQUIRED" };
    return { state: "CONDITIONAL", tone: "amber", label: "CONDITIONAL" };
  }, [data]);

  if (loading) {
    return (
      <Card className="p-5 border-slate-200" data-testid="live-readiness-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking platform readiness…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden) return null;
  if (data.error) {
    return (
      <Card className="p-5 border-rose-200 bg-rose-50" data-testid="live-readiness-error">
        <div className="text-sm text-rose-800">Readiness unavailable: {data.error}</div>
      </Card>
    );
  }

  const toneMap = {
    emerald: { ring: "text-emerald-600", chip: "bg-emerald-600", band: "border-emerald-300 bg-emerald-50", text: "text-emerald-800" },
    amber: { ring: "text-amber-600", chip: "bg-amber-500", band: "border-amber-300 bg-amber-50", text: "text-amber-800" },
    rose: { ring: "text-rose-600", chip: "bg-rose-600", band: "border-rose-300 bg-rose-50", text: "text-rose-800" },
  };
  const ov = toneMap[overall?.tone || "amber"];

  return (
    <Card className="p-5 border border-slate-200 bg-white flex flex-col" data-testid="live-readiness-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className={`h-5 w-5 shrink-0 ${ov.ring}`} />
          <h3 className="font-semibold text-slate-900 truncate">Platform Readiness</h3>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="live-readiness-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Overall verdict summary card */}
      <div className={`rounded-lg border ${ov.band} px-4 py-3 mb-4`} data-testid="live-readiness-overall">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Overall status</div>
            <div className={`font-heading font-black text-xl leading-tight ${ov.text}`} data-testid="live-readiness-overall-state">
              {overall?.label}
            </div>
          </div>
          <span className={`text-[11px] text-white px-2.5 py-1 rounded-full font-semibold shrink-0 ${ov.chip}`} data-testid="live-readiness-percent">
            {data.completed}/{data.total} systems · {data.percent}%
          </span>
        </div>
      </div>

      {/* Priority-grouped status cards */}
      <div className="space-y-4 flex-1">
        {GROUPS.map((g) => {
          const items = sortedChecks.filter((c) => c.meta.group === g.key);
          if (items.length === 0) return null;
          return (
            <div key={g.key} data-testid={`live-readiness-group-${g.key}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{g.label}</div>
              <div className="space-y-2">
                {items.map((c) => {
                  const ok = c.done;
                  const severity = ok ? "ok" : (c.meta.group === "critical" ? "critical" : "warn");
                  const sv = ok
                    ? { border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-800", icon: "text-emerald-600", label: "READY" }
                    : severity === "critical"
                    ? { border: "border-rose-200", badge: "bg-rose-100 text-rose-800", icon: "text-rose-600", label: "ACTION REQUIRED" }
                    : { border: "border-amber-200", badge: "bg-amber-100 text-amber-800", icon: "text-amber-600", label: "ATTENTION" };
                  const Icon = c.meta.Icon;
                  return (
                    <div
                      key={c.id}
                      className={`rounded-lg border ${sv.border} bg-white p-3`}
                      data-testid={`live-readiness-${c.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${sv.icon}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="text-sm font-semibold text-slate-900">{c.meta.name}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${sv.badge}`} data-testid={`live-readiness-${c.id}-status`}>
                              {ok ? <CheckCircle2 className="h-3 w-3 inline -mt-0.5 mr-0.5" /> : <AlertTriangle className="h-3 w-3 inline -mt-0.5 mr-0.5" />}
                              {sv.label}
                            </span>
                          </div>
                          <p className="text-xs leading-snug text-slate-600">
                            {ok ? c.meta.okMeaning : c.meta.failMeaning}
                          </p>
                          {!ok && (
                            <>
                              <p className="text-[11px] leading-snug text-slate-500 mt-1">{c.hint}</p>
                              <button
                                onClick={() => onManage?.()}
                                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                                data-testid={`live-readiness-${c.id}-manage`}
                              >
                                Manage Integration <ChevronRight className="h-3 w-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Email outcomes · last 24h (existing functionality, preserved) */}
      {data.emails24h && (
        <div className="mt-4 pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Email outcomes · last 24h</div>
            <span className="text-[10px] text-slate-400">tap to inspect</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => openDrill("sent")} className="rounded-md p-2.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-left transition-colors" data-testid="live-readiness-emails-sent">
              <div className="flex items-center gap-1.5"><MailCheck className="h-3.5 w-3.5 text-emerald-700" /><span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800">Sent</span></div>
              <div className="font-heading font-black text-2xl text-emerald-900 mt-0.5">{data.emails24h.sent}</div>
            </button>
            <button onClick={() => openDrill("failed")} className="rounded-md p-2.5 border border-rose-200 bg-rose-50 hover:bg-rose-100 text-left transition-colors" data-testid="live-readiness-emails-failed">
              <div className="flex items-center gap-1.5"><MailX className="h-3.5 w-3.5 text-rose-700" /><span className="text-[10px] uppercase tracking-wider font-semibold text-rose-800">Failed</span></div>
              <div className="font-heading font-black text-2xl text-rose-900 mt-0.5">{data.emails24h.failed}</div>
            </button>
            <button onClick={() => openDrill("skipped")} className="rounded-md p-2.5 border border-amber-200 bg-amber-50 hover:bg-amber-100 text-left transition-colors" data-testid="live-readiness-emails-skipped">
              <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-amber-700" /><span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">Skipped</span></div>
              <div className="font-heading font-black text-2xl text-amber-900 mt-0.5">{data.emails24h.skipped}</div>
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!drill} onOpenChange={(v) => { if (!v) setDrill(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="live-readiness-drill-dialog">
          <DialogHeader>
            <DialogTitle>Email events · {drill?.outcome} · last 24h</DialogTitle>
          </DialogHeader>
          {drill?.loading ? (
            <div className="py-6 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</div>
          ) : drill?.events?.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500" data-testid="live-readiness-drill-empty">No {drill?.outcome} email events in the last 24h.</div>
          ) : (
            <div className="space-y-1.5">
              {(drill?.events || []).map((e, i) => (
                <div key={i} className="rounded-md p-2.5 bg-slate-50 border border-slate-100 text-xs" data-testid={`live-readiness-drill-row-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{e.category || "—"}</span>
                    <span className="text-slate-900 font-medium">{e.recipient || "(no recipient)"}</span>
                    <span className="ml-auto text-[10px] text-slate-400">{fmtRel(e.createdAt)}</span>
                  </div>
                  {e.error && <div className="text-[11px] text-rose-700 mt-0.5 break-words">{e.error}</div>}
                  {e.ref && <div className="text-[10px] text-slate-400 mt-0.5 font-mono">ref: {e.ref}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
